import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  NumberInput,
  Pagination,
  Stack,
  Text,
} from '@mantine/core';
import { useState } from 'react';

import { AuthoringInstruction } from '../components/AuthoringInstruction';
import { InfoTip } from '../components/InfoTip';
import { SkillPicker } from '../components/skill-picker';
import { useBackfillSkill, useCoverageGaps } from '../hooks/use-content';
import { useEnumLabel, usePlural, useT } from '../i18n';
import type { CoverageGapCell } from '../lib/api';
import { errorMessage } from '../lib/ui';

/**
 * Generate questions for a skill (PRE-284).
 *
 * The screen asks one question, because one question is all an operator can
 * actually answer: WHICH SKILL. It used to ask five — skill, type, difficulty,
 * count, prompt template — and the other four were the panel handing back
 * decisions it had already computed:
 *
 *   - type and difficulty: a skill needs items across all of them, so choosing
 *     one is a silent decision to leave the other five cells empty;
 *   - count: `content.coverageFloor` and the cell's deficit already say how many
 *     are missing, and a typed number overwrites that with a guess;
 *   - prompt template: meaningless to the reader, and picking anything but the
 *     active default runs generation through a variant nobody is comparing.
 *
 * So the button now drives the backfill queue (PRE-174) instead of the
 * single-cell synchronous route: every below-floor cell of the chosen skill,
 * ≥1 MCQ each, filled to the floor. That path was always the correct one — the
 * form was the only reason it was not being used.
 *
 * It returns as soon as the work is QUEUED. Nothing on this screen may report
 * drafts as written: they appear in the review queue as the consumer gets to
 * them, and claiming otherwise would be a screen inventing progress.
 */
/** PRE-285: cells per page of the worklist. */
const PAGE_SIZE = 20;

export function GenerateScreen() {
  const t = useT();
  const plural = usePlural();
  const label = useEnumLabel();
  const [page, setPage] = useState(1);
  const gapsQuery = useCoverageGaps({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
  const backfill = useBackfillSkill();
  /**
   * A second mutation instance for the bulk action, so a preview never lands in
   * the single-skill card's result slot and read as work that was queued.
   */
  const bulk = useBackfillSkill();
  const preview = bulk.data?.dryRun ? bulk.data : null;
  const queued = bulk.data && !bulk.data.dryRun ? bulk.data : null;

  const [skillId, setSkillId] = useState('');
  /** PRE-285: how many top-ranked cells the bulk action covers. */
  const [bulkCells, setBulkCells] = useState(10);
  const gaps = gapsQuery.data?.gaps ?? [];
  const floor = gapsQuery.data?.floor ?? 5;
  const totalCells = gapsQuery.data?.totalCells ?? 0;
  const totalDeficit = gapsQuery.data?.totalDeficit ?? 0;
  const pages = Math.max(1, Math.ceil(totalCells / PAGE_SIZE));
  const result = backfill.data ?? null;

  /**
   * What the chosen skill is still missing.
   *
   * Read off the page currently loaded, so it is only shown when that skill's
   * cells are on it — the alternative is a number computed from a page and
   * presented as the whole answer.
   */
  const skillGaps = gaps.filter((g) => g.skillId === skillId);
  const missing = skillGaps.reduce((sum, g) => sum + g.deficit, 0);

  function pickGap(cell: CoverageGapCell) {
    setSkillId(cell.skillId);
    backfill.reset();
  }

  return (
    <Stack gap="md">
      <Card withBorder padding="lg" radius="md">
        <Group gap={6} mb={4}>
          <Text fw={600}>{t('generate.forSkill')}</Text>
          <InfoTip label={t('generate.forSkillTip', { floor })} />
        </Group>
        <Text c="dimmed" size="sm" mb="md">
          {t('generate.forSkillHint')}
        </Text>

        <Group align="flex-end" gap="sm" wrap="wrap">
          <SkillPicker
            label={t('common.skill')}
            value={skillId || null}
            onChange={(v) => {
              setSkillId(v ?? '');
              backfill.reset();
            }}
          />
          <Button
            onClick={() => backfill.mutate({ skillId, maxCells: 20, dryRun: false })}
            loading={backfill.isPending}
            disabled={!skillId}
          >
            {t('generate.run')}
          </Button>
        </Group>

        {skillId && skillGaps.length > 0 && !backfill.isPending && !result && (
          <Text c="dimmed" size="sm" mt="sm">
            {plural('generate.missing_one', 'generate.missing', missing, {
              cells: skillGaps.length,
            })}
          </Text>
        )}

        {backfill.isError && (
          <Text c="red" size="sm" mt="sm">
            {errorMessage(backfill.error)}
          </Text>
        )}
      </Card>

      {result && (
        <Alert color={result.cells.length > 0 ? 'blue' : 'gray'} variant="light">
          <Text fw={600} mb={4}>
            {result.cells.length > 0
              ? plural('generate.queued_one', 'generate.queued', result.enqueued ?? 0, {
                  jobs: result.enqueued ?? 0,
                  cells: result.cells.length,
                })
              : t('generate.nothingToQueue')}
          </Text>
          {result.cells.map((cell) => (
            <Text key={`${cell.skillId}-${cell.difficulty}`} size="sm">
              {t('generate.cellLine', {
                difficulty: label('difficulty', cell.difficulty),
                deficit: cell.deficit,
                steps: cell.steps.map((s) => `${s.count}× ${label('type', s.type)}`).join(', '),
              })}
            </Text>
          ))}
          {result.cells.length > 0 && (
            <Text c="dimmed" size="sm" mt="xs">
              {t('generate.draftsArrive')}
            </Text>
          )}
        </Alert>
      )}

      {/*
        PRE-285: the same route, unnarrowed — it walks the ranking itself. The
        preview is not decoration: unlike one skill's three cells, this is an
        open-ended spend, and `dryRun` already returns the plan for free. A
        button that queues an unknown number of model calls teaches you its
        number the first time it is the wrong one.
      */}
      <Card withBorder padding="lg" radius="md">
        <Group gap={6} mb={4}>
          <Text fw={600}>{t('generate.bulkTitle')}</Text>
          <InfoTip label={t('generate.bulkTip')} />
        </Group>
        <Text c="dimmed" size="sm" mb="md">
          {plural('generate.bulkSummary_one', 'generate.bulkSummary', totalCells, {
            cells: totalCells,
            deficit: totalDeficit,
          })}
        </Text>
        <Group align="flex-end" gap="sm" wrap="wrap">
          <NumberInput
            label={t('generate.cellsToFill')}
            description={t('generate.cellsToFillHint')}
            min={1}
            max={20}
            w={180}
            value={bulkCells}
            onChange={(v) => {
              setBulkCells(Math.min(20, Math.max(1, Number(v) || 1)));
              bulk.reset();
            }}
          />
          <Button
            variant="default"
            onClick={() => bulk.mutate({ maxCells: bulkCells, dryRun: true })}
            loading={bulk.isPending && bulk.variables?.dryRun === true}
          >
            {t('generate.preview')}
          </Button>
          <Button
            onClick={() => bulk.mutate({ maxCells: bulkCells, dryRun: false })}
            loading={bulk.isPending && bulk.variables?.dryRun === false}
            disabled={!preview || preview.cells.length === 0}
          >
            {preview
              ? plural('generate.queueJobs_one', 'generate.queueJobs', preview.jobs ?? 0)
              : t('generate.queue')}
          </Button>
        </Group>

        {bulk.isError && (
          <Text c="red" size="sm" mt="sm">
            {errorMessage(bulk.error)}
          </Text>
        )}

        {preview && (
          <Alert color="gray" variant="light" mt="md">
            <Text size="sm">
              {plural('generate.previewLine_one', 'generate.previewLine', preview.cells.length, {
                cells: preview.cells.length,
                drafts: preview.cells.reduce((n, c) => n + c.deficit, 0),
              })}
            </Text>
            {preview.cells.slice(0, 8).map((cell) => (
              <Text key={`${cell.skillId}-${cell.difficulty}`} size="sm" c="dimmed">
                {t('generate.previewCell', {
                  skill: cell.skillTitle,
                  difficulty: label('difficulty', cell.difficulty),
                  deficit: cell.deficit,
                })}
              </Text>
            ))}
            {preview.cells.length > 8 && (
              <Text size="sm" c="dimmed">
                {t('generate.previewMore', { count: preview.cells.length - 8 })}
              </Text>
            )}
          </Alert>
        )}

        {queued && (
          <Alert color="blue" variant="light" mt="md">
            {plural('generate.queuedBulk_one', 'generate.queuedBulk', queued.enqueued ?? 0, {
              jobs: queued.enqueued ?? 0,
              cells: queued.cellsPlanned ?? 0,
            })}
          </Alert>
        )}
      </Card>

      {gaps.length > 0 && (
        <Card withBorder padding="lg" radius="md">
          <Group gap={6} mb={4}>
            <Text fw={600}>{t('generate.thinnest')}</Text>
            <InfoTip label={t('generate.thinnestTip')} />
          </Group>
          <Text c="dimmed" size="sm" mb="sm">
            {t('generate.legend', { floor })}
          </Text>
          <Text c="dimmed" size="sm" mb="sm">
            {t('bank.range', {
              from: (page - 1) * PAGE_SIZE + 1,
              to: Math.min(page * PAGE_SIZE, totalCells),
              total: totalCells,
            })}
          </Text>
          <Stack gap={0}>
            {gaps.map((g) => (
              <Group
                key={`${g.skillId}-${g.difficulty}`}
                justify="space-between"
                wrap="nowrap"
                py="xs"
                style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}
              >
                <Group gap="xs" wrap="wrap" style={{ minWidth: 0 }}>
                  <Badge variant="light" color="gray" radius="sm" tt="capitalize">
                    {label('difficulty', g.difficulty)}
                  </Badge>
                  <Text fw={600} span>
                    {g.skillTitle}
                  </Text>
                  <Text c="dimmed" size="sm" span>
                    {t('generate.cellCounts', {
                      published: g.published,
                      target: g.target,
                      deficit: g.deficit,
                      demand: g.demand > 0 ? t('generate.demandSuffix', { demand: g.demand }) : '',
                    })}
                  </Text>
                </Group>
                <Button size="xs" variant="default" onClick={() => pickGap(g)}>
                  {t('generate.select')}
                </Button>
              </Group>
            ))}
          </Stack>

          {pages > 1 && (
            <Group justify="center" mt="md">
              <Pagination value={page} onChange={setPage} total={pages} siblings={1} withEdges />
            </Group>
          )}
        </Card>
      )}

      <AuthoringInstruction />
    </Stack>
  );
}
